/**
 * Support Configuration Dialog
 * Modal for selecting support position and type when adding support to a node
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  SupportPosition,
  SupportType,
  SUPPORT_POSITION_LABELS,
  SUPPORT_TYPE_LABELS,
  DEFAULT_SUPPORT_CONFIG,
  DegreesOfFreedom,
} from "../../types/support";
import { useSupportStore } from "../../store/useSupportStore";

interface SupportConfigDialogProps {
  visible: boolean;
  nodeId: string | null;
  onConfirm: (
    nodeIds: string[],
    position: SupportPosition,
    type: SupportType,
    dof?: DegreesOfFreedom
  ) => void;
  onCancel: () => void;
}

export const SupportConfigDialog: React.FC<SupportConfigDialogProps> = ({
  visible,
  nodeId,
  onConfirm,
  onCancel,
}) => {
  const [selectedPosition, setSelectedPosition] = useState<SupportPosition>(
    DEFAULT_SUPPORT_CONFIG.position
  );
  const [selectedType, setSelectedType] = useState<SupportType>(
    DEFAULT_SUPPORT_CONFIG.type
  );
  const [customDOF, setCustomDOF] = useState<DegreesOfFreedom>({
    Fx: 0,
    Fy: 0,
    Fz: 0,
    Rx: 0,
    Ry: 0,
    Rz: 0,
  });
  const [nodeIds, setNodeIds] = useState<string>("");
  const supportStore = useSupportStore();

  // Draggable functionality states
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const dialogRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);

  // Check if the initially clicked node has existing support
  const existingSupportForNode = nodeId
    ? supportStore.getSupportsByNodeId(nodeId)[0]
    : null;

  // Initialize dialog when opened
  useEffect(() => {
    if (visible && nodeId) {
      setNodeIds(nodeId);

      const nodeSupport = supportStore.getSupportsByNodeId(nodeId)[0];

      if (nodeSupport) {
        // Load existing support values from clicked node
        setSelectedPosition(nodeSupport.position);
        setSelectedType(nodeSupport.type);
        setCustomDOF(
          nodeSupport.dof ?? { Fx: 0, Fy: 0, Fz: 0, Rx: 0, Ry: 0, Rz: 0 }
        );
      } else {
        // Reset to defaults for new support
        setSelectedPosition(DEFAULT_SUPPORT_CONFIG.position);
        setSelectedType(DEFAULT_SUPPORT_CONFIG.type);
        setCustomDOF({ Fx: 0, Fy: 0, Fz: 0, Rx: 0, Ry: 0, Rz: 0 });
      }
    }
  }, [visible, nodeId, supportStore]);

  const handleConfirm = () => {
    // Parse comma-separated node IDs and clean them
    const parsedNodeIds = nodeIds
      .split(",")
      .map((id) => id.trim())
      .filter((id) => id !== "");

    // Allow submission even if parsedNodeIds is empty
    // Only pass DOF for fixed support type
    const dofToPass = selectedType === "fixed" ? customDOF : undefined;
    onConfirm(parsedNodeIds, selectedPosition, selectedType, dofToPass);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleConfirm();
    } else if (e.key === "Escape") {
      onCancel();
    }
  };

  // Draggable functionality handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    // Allow dragging from header but not from close button
    if (
      headerRef.current &&
      headerRef.current.contains(target) &&
      !target.closest("button")
    ) {
      setIsDragging(true);
      // Store the offset from mouse position to dialog top-left corner
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      });
    }
  };

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (isDragging) {
        // Calculate new position based on mouse position minus initial offset
        let newX = e.clientX - dragStart.x;
        let newY = e.clientY - dragStart.y;

        // Get dialog dimensions
        const dialogWidth = dialogRef.current?.offsetWidth || 384;

        // Set boundaries - allow some overlap but keep dialog mostly visible
        const minX = -dialogWidth + 100; // Keep at least 100px visible on left
        const maxX = window.innerWidth - 100; // Keep at least 100px visible on right
        const minY = 0; // Don't go above viewport
        const maxY = window.innerHeight - 100; // Keep at least 100px visible at bottom

        // Apply constraints
        newX = Math.max(minX, Math.min(newX, maxX));
        newY = Math.max(minY, Math.min(newY, maxY));

        setPosition({ x: newX, y: newY });
      }
    },
    [isDragging, dragStart]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Add/remove global mouse event listeners
  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "grabbing";
      document.body.style.userSelect = "none";
    } else {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);
  // Reset position when dialog opens - center it precisely
  useEffect(() => {
    if (visible) {
      // Calculate precise center based on dialog dimensions
      const dialogWidth = 400; // w-96 = 384px
      const dialogHeight = 400; // Approximate height with all content
      // Move the dialog slightly upward so it sits a bit higher than strict center
      const verticalOffset = 110; // px to move up from center — adjust if needed
      setPosition({
        x: Math.max(0, Math.floor((window.innerWidth - dialogWidth) / 2)),
        y: Math.max(
          0,
          Math.floor((window.innerHeight - dialogHeight) / 2) - verticalOffset
        ),
      });
    }
  }, [visible]);

  if (!visible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 z-50">
      <div
        ref={dialogRef}
        className="absolute bg-white rounded-lg shadow-xl p-6 w-96 max-w-md"
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
          cursor: isDragging ? "grabbing" : "default",
        }}
        onKeyDown={handleKeyDown}
        onMouseDown={handleMouseDown}
        tabIndex={-1}
      >
        {/* Header - Draggable Blue Bar */}
        <div
          ref={headerRef}
          className="flex justify-between items-center px-4 py-3 -mx-6 -mt-6 mb-6 bg-blue-600 rounded-t-lg cursor-grab active:cursor-grabbing select-none"
          style={{ cursor: isDragging ? "grabbing" : "grab" }}
        >
          <h2 className="text-xl font-semibold text-white pointer-events-none">
            Configure Support(s)
          </h2>
          <button
            onClick={onCancel}
            className="text-white hover:text-gray-200 text-xl font-bold transition-colors duration-200 p-1 hover:bg-blue-700 rounded"
            style={{ cursor: "pointer" }}
          >
            ×
          </button>
        </div>
        {/* Multi-Node Selection */}
        {/* <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Node IDs (comma-separated)
          </label>
          <input
            type="text"
            value={nodeIds}
            onChange={(e) => setNodeIds(e.target.value)}
            placeholder="e.g., 1,2,3,4"
            className="w-full px-3 py-2 text-sm text-black border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <p className="text-xs text-gray-500 mt-1">
            Enter node IDs separated by commas. Support will be applied to all
            specified nodes.
          </p>
        </div> */}

        {/* Support Type Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Support Type
          </label>

          {/* Dropdown (replaces radio buttons) */}
          <select
            name="supportType"
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value as SupportType)}
            className="w-full px-3 py-2 text-sm text-black border border-gray-300 rounded-md 
               focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {Object.entries(SUPPORT_TYPE_LABELS).map(([type, label]) => (
              <option key={type} value={type}>
                {label}
              </option>
            ))}
          </select>

          {/* Description block now matches selectedType */}
          <p className="text-xs text-gray-500 mt-2">
            {selectedType === "fixed" &&
              "All degrees of freedom can be customized (translations & rotations)"}
            {selectedType === "pinned" &&
              "Translations fixed, rotations free (predefined constraints)"}
            {selectedType === "roller" &&
              "Y-translation fixed, all others free (predefined constraints)"}
          </p>
        </div>

        {/* Degrees of Freedom Configuration - Only for Fixed Support */}
        {selectedType === "fixed" && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Degrees of Freedom (DOF)
            </label>
            <p className="text-xs text-gray-500 mb-3">
              0 = Fixed (no arrow), 1 = Free (colored arrow), Other = Spring
              (black spiral)
            </p>

            <div className="grid grid-cols-2 gap-4 p-3 bg-gray-50 rounded">
              <div>
                <p className="text-xs font-semibold text-gray-600 mb-2">
                  Translation
                </p>
                <div className="space-y-1">
                  {["Fx", "Fy", "Fz"].map((dof) => (
                    <div
                      key={dof}
                      className="flex items-center justify-between"
                    >
                      <span className="text-xs text-gray-600">{dof}:</span>
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        max="10"
                        value={customDOF[dof as keyof DegreesOfFreedom]}
                        onChange={(e) => {
                          setCustomDOF((prev) => ({
                            ...prev,
                            [dof]: parseFloat(e.target.value) || 0,
                          }));
                        }}
                        className="w-16 px-1 py-0 text-xs border rounded text-black"
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-xs font-semibold text-gray-600 mb-2">
                  Rotation
                </p>
                <div className="space-y-1">
                  {["Rx", "Ry", "Rz"].map((dof) => (
                    <div
                      key={dof}
                      className="flex items-center justify-between"
                    >
                      <span className="text-xs text-gray-600">{dof}:</span>
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        max="10"
                        value={customDOF[dof as keyof DegreesOfFreedom]}
                        onChange={(e) => {
                          setCustomDOF((prev) => ({
                            ...prev,
                            [dof]: parseFloat(e.target.value) || 0,
                          }));
                        }}
                        className="w-16 px-1 py-0 text-xs border rounded text-black"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Support Position Selection (dropdown) */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Support Position
          </label>
          <div>
            <select
              name="supportPosition"
              value={selectedPosition}
              onChange={(e) =>
                setSelectedPosition(e.target.value as SupportPosition)
              }
              className="w-full px-3 py-2 text-sm text-black border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {Object.entries(SUPPORT_POSITION_LABELS).map(
                ([position, label]) => (
                  <option key={position} value={position}>
                    {label}
                  </option>
                )
              )}
            </select>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Default: {SUPPORT_POSITION_LABELS[DEFAULT_SUPPORT_CONFIG.position]}
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              if (nodeIds.trim()) {
                const parsedNodeIds = nodeIds
                  .split(",")
                  .map((id) => id.trim())
                  .filter((id) => id !== "");
                parsedNodeIds.forEach((id) => {
                  // supportStore.removeSupportsByNodeId(id);
                });
                onCancel();
              }
            }}
            className="px-4 py-2 text-sm font-medium text-red-700 bg-red-100 rounded hover:bg-red-200 transition-colors"
          >
            Remove Support(s)
          </button>
          <button
            onClick={handleConfirm}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-500 rounded hover:bg-blue-600 transition-colors"
          >
            {existingSupportForNode ? "Update Support(s)" : "Add Support(s)"}
          </button>
        </div>
      </div>
    </div>
  );
};
